import { useMany, useTranslate, type CrudFilter } from '@refinedev/core';
import { List, DateField } from '@refinedev/antd';
import { Alert, Table, Flex, Tooltip, Typography } from 'antd';
import type {
  ChargeMonthlyOut,
  ProfileOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { ProfileSelectFilter } from '@/components/account/ProfileSelectFilter';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { MonthFilter } from '@/components/common/MonthFilter';
import { orDash } from '@/components/common/show/shared';
import { ProductFormatTag } from '@/components/user_catalog/publications/ProductFormatTag';
import { ProductSelectFilter } from '@/components/user_catalog/publications/ProductSelectFilter';
import { productKind } from '@/components/user_catalog/publications/productKind';
import { usePublicationsByProducts } from '@/components/user_catalog/publications/usePublicationsByProducts';
import { PublisherSelectFilter } from '@/components/user_catalog/publishers/PublisherSelectFilter';
import { useAdminTable } from '@/hooks/useAdminTable';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';
import { formatDuration } from '@/utils/duration';
import { formatLedger, formatPayable } from '@/utils/money';
import { HintLabel } from '@/components/common/HintLabel';
import { UnitPriceCell } from '@/components/settlement/UnitPriceCell';
import { NUMERIC_STYLE } from '@/components/settlement/numeric';

const T = 'settlement.charges_monthly';

export const ChargesMonthlyList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();

  const { tableProps, sorters, filters, setFilters } =
    useAdminTable<ChargeMonthlyOut>({
      pagination: { pageSize: 20 },
      resource: 'settlement.charges_monthly',
      sorters: {
        initial: [{ field: 'bucket', order: 'desc' }],
      },
      syncWithLocation: true,
    });

  const rows = tableProps.dataSource ?? [];

  // Batched once per page: the row components take the record and skip their
  // own fetch, so a page costs one extra request instead of one per row.
  const publisherIds = [
    ...new Set(rows.map((r) => r.publisher_id).filter(Boolean)),
  ];

  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });

  const publisherMap = new Map<number, PublisherListOut>(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  const profileIds = [
    ...new Set(rows.map((r) => r.profile_id).filter(Boolean)),
  ];

  const { result: profilesResult } = useMany<ProfileOut>({
    resource: 'account.profiles',
    ids: profileIds,
    queryOptions: { enabled: profileIds.length > 0 },
  });

  const profileMap = new Map<number, ProfileOut>(
    (profilesResult?.data ?? []).map((p: ProfileOut) => [p.id, p])
  );

  const productIds = [
    ...new Set(rows.map((r) => r.product_id).filter(Boolean)),
  ];

  const publicationByProduct = usePublicationsByProducts(productIds);

  return (
    <List canCreate={false}>
      <Alert
        type="warning"
        showIcon
        message={t(`${T}.intro`)}
        style={{ marginBottom: 16 }}
      />
      <Table
        {...rowNav('settlement.charges_monthly', tableProps)}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        scroll={{ x: 'max-content' }}
      >
        <Table.Column
          dataIndex="bucket"
          title={
            <HintLabel
              label={t(`${T}.fields.bucket`)}
              hint={t(`${T}.bucket_hint`)}
            />
          }
          fixed="left"
          sorter
          sortOrder={getSortOrder(sorters, 'bucket')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'bucket')}
          filterDropdown={({ confirm, clearFilters }) => (
            <MonthFilter
              field="bucket"
              placeholder={t(`${T}.fields.bucket`)}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => (
            <DateField value={value} format="YYYY-MM" />
          )}
        />
        <Table.Column
          dataIndex="product_id"
          title={t(`${T}.fields.product_id`)}
          fixed="left"
          width={320}
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <ProductSelectFilter
              field="product_id"
              placeholder={t(`${T}.fields.product_id`)}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number, record: ChargeMonthlyOut) => {
            const publication = publicationByProduct.get(value);
            const kind = productKind(publication, value);
            return orDash(
              value != null && (
                <Flex vertical gap={4} align="flex-start">
                  <Flex gap={4} align="center" wrap>
                    {publication ? (
                      <PublicationInfoPopover
                        publication={publication}
                        publicationId={publication.id}
                      />
                    ) : (
                      value
                    )}
                    <Typography.Text type="secondary">
                      {`v${record.product_version}`}
                    </Typography.Text>
                  </Flex>
                  {kind && <ProductFormatTag kind={kind} />}
                </Flex>
              )
            );
          }}
        />
        <Table.Column
          dataIndex="publisher_id"
          title={t(`${T}.fields.publisher_id`)}
          filtered={isFieldFiltered(filters as CrudFilter[], 'publisher_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublisherSelectFilter
              field="publisher_id"
              only="imprints"
              placeholder={t(`${T}.fields.publisher_id`)}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number) =>
            orDash(
              value != null && (
                <PublisherIconLink
                  publisherId={value}
                  publisher={publisherMap.get(value)}
                />
              )
            )
          }
        />
        <Table.Column
          dataIndex="profile_id"
          title={t(`${T}.fields.profile_id`)}
          filtered={isFieldFiltered(filters as CrudFilter[], 'profile_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <ProfileSelectFilter
              field="profile_id"
              placeholder={t(`${T}.fields.profile_id`)}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number) =>
            orDash(
              value != null && (
                <ProfileIconLink
                  profileId={value}
                  profile={profileMap.get(value)}
                />
              )
            )
          }
        />
        <Table.Column
          dataIndex="product_duration"
          title={t(`${T}.fields.product_duration`)}
          render={(value: number) => (
            <Tooltip title={`${value}s`}>{formatDuration(value)}</Tooltip>
          )}
        />
        <Table.Column
          dataIndex="rate_value"
          title={t(`${T}.fields.rate_value`)}
          align="right"
          render={(value: string, record: ChargeMonthlyOut) => (
            <span style={NUMERIC_STYLE}>
              {formatLedger(value, record.currency)}
            </span>
          )}
        />
        {/* Column titles drop the "unit price" words — the group header
            carries them. */}
        <Table.ColumnGroup title={t(`${T}.fields.unit_price`)}>
          <Table.Column
            dataIndex="unit_price"
            title={
              <HintLabel
                label={t(`${T}.columns.ledger`)}
                hint={t('settlement.hints.unit_price')}
              />
            }
            align="right"
            render={(value: string, record: ChargeMonthlyOut) => (
              <UnitPriceCell unitPrice={value} currency={record.currency} />
            )}
          />
          <Table.Column
            dataIndex="payable_unit_price"
            title={
              <HintLabel
                label={t(`${T}.columns.invoice`)}
                hint={t('settlement.hints.payable_unit_price')}
              />
            }
            align="right"
            render={(value: string, record: ChargeMonthlyOut) => (
              <span style={NUMERIC_STYLE}>
                {formatPayable(value, record.currency)}
              </span>
            )}
          />
        </Table.ColumnGroup>
        <Table.Column
          dataIndex="basis_seconds"
          title={t(`${T}.fields.basis_seconds`)}
          sorter
          sortOrder={getSortOrder(sorters, 'basis_seconds')}
          render={(value: number) => (
            <Tooltip title={`${value}s`}>{formatDuration(value)}</Tooltip>
          )}
        />
        <Table.Column
          dataIndex="charges_count"
          title={t(`${T}.fields.charges_count`)}
          width={90}
          sorter
          sortOrder={getSortOrder(sorters, 'charges_count')}
        />
        {/* A charge carries a price but no invoice amount: the row still splits
            by account and profile, below the invoice's grain. The label stays
            the plain `amount`, so the hint is what explains the four places. */}
        <Table.Column
          dataIndex="amount"
          title={
            <HintLabel
              label={t(`${T}.fields.amount`)}
              hint={t('settlement.hints.amount_ledger')}
            />
          }
          align="right"
          fixed="right"
          sorter
          sortOrder={getSortOrder(sorters, 'amount')}
          render={(value: string, record: ChargeMonthlyOut) => (
            <span style={NUMERIC_STYLE}>
              {formatLedger(value, record.currency)}
            </span>
          )}
        />
      </Table>
    </List>
  );
};
